import type { ActivitiesFinalized } from "@b2bportal/activities-api";
import type { OrderedFlight } from "@b2bportal/air-booking-api";
import type { GroundBookingResult } from "@b2bportal/ground-api";
import type { HomesReservation } from "@b2bportal/homes-api";
import type { FinalizedLodging } from "@b2bportal/lodging-api";
import type { PackageFinalizedResult } from "@b2bportal/packages-api";
import { Product, type ProductOpaqueValue } from "@b2bportal/purchase-api";
import type { DoneInvokeEvent } from "xstate";

/**
 * The XState `onDone` event of the fulfill poll. Typed loosely because the
 * same getters serve the success and failure tracking actions, and the failure
 * event carries an error payload instead of a `FulfillSuccess`.
 */
type FulfillEvent = DoneInvokeEvent<unknown> | undefined;

const getFulfilledProducts = (
  event: FulfillEvent
): ProductOpaqueValue[] | undefined => {
  const data = event?.data;
  if (typeof data !== "object" || data === null || !("products" in data)) {
    return undefined;
  }
  return Array.isArray(data.products) ? data.products : undefined;
};

/**
 * User-facing confirmation ids read straight from the fulfill response, for
 * the `confirmation_id` GTM property on `complete_buy_*` events.
 *
 * Why the event and not machine context: the `complete_buy_*` tracking action
 * runs first in the fulfill `onDone` actions, before `fulfilledProducts` is
 * written to context, so the event payload is the only source at that point.
 *
 * Each getter picks the same id the confirmation page displays. Known
 * exception: cars from Sixt, where the fulfill `bookingId` is the provider
 * UUID while the page shows the supplier display number (accepted, see the
 * RBC-2374 investigation report).
 */
const findFulfilledValue = <T>(
  event: FulfillEvent,
  ...types: Product[]
): T | undefined =>
  getFulfilledProducts(event)?.find((product) => types.includes(product.type))
    ?.value;

/**
 * Hotel: external reservation id (`customerReservationId`), the same value the
 * confirmation page shows as `reservation.reservationId`. Not the Hopper
 * internal `reservationBookingId`.
 *
 * @returns Undefined when the event has no hotel product.
 */
export const getFulfilledHotelConfirmationId = (event: FulfillEvent) =>
  findFulfilledValue<FinalizedLodging>(event, Product.Hotel)
    ?.customerReservationId;

/**
 * Flights: the b2b locator, for both single and multi-provider itineraries.
 *
 * @returns Undefined when the event has no flight product.
 */
export const getFulfilledFlightConfirmationId = (event: FulfillEvent) =>
  findFulfilledValue<OrderedFlight>(
    event,
    Product.Flight,
    Product.MultiProviderFlight
  )?.travelItinerary?.locators?.b2b;

/**
 * Cars: the user-facing booking code (`bookingId`), not the internal
 * `groundBookingId`. See the Sixt caveat on this module.
 *
 * @returns Undefined when the event has no ground product.
 */
export const getFulfilledCarConfirmationId = (event: FulfillEvent) =>
  findFulfilledValue<GroundBookingResult>(event, Product.Ground)?.bookingId;

/**
 * Homes: the reservation id, same source as `getHomesConfirmationId`.
 *
 * @returns Undefined when the event has no homes product.
 */
export const getFulfilledHomeConfirmationId = (event: FulfillEvent) =>
  findFulfilledValue<HomesReservation>(event, Product.Homes)?.id?.value;

/**
 * Activities: the customer reservation id shown on the confirmation page.
 *
 * @returns Undefined when the event has no activities product.
 */
export const getFulfilledActivityConfirmationId = (event: FulfillEvent) =>
  findFulfilledValue<ActivitiesFinalized>(event, Product.Activities)
    ?.customerReservationId;

/**
 * Packages: the customer reservation id, which is the same value the itinerary
 * endpoint returns as `customerConfirmationId` and the value already sent to
 * HTS as `locator`. Not the internal `hopperReservationId`.
 *
 * @returns Undefined when the event has no package product.
 */
export const getFulfilledPackageConfirmationId = (event: FulfillEvent) =>
  findFulfilledValue<PackageFinalizedResult>(event, Product.Package)
    ?.customerReservationId;
